import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface ProductCardProps {
  product: {
    id: string;
    name: string;
    manufacturer: string;
    category: string;
    image: string;
    alt: string;
    price: string;
    moq: string;
    availability: 'In Stock' | 'Low Stock' | 'Out of Stock';
    certifications: string[];
    rating: number;
    verified: boolean;
  };
  onInquiry: (productId: string) => void;
  onSave: (productId: string) => void;
  isSaved: boolean;
}

const ProductCard = ({ product, onInquiry, onSave, isSaved }: ProductCardProps) => {
  const getAvailabilityColor = (status: string) => {
    switch (status) {
      case 'In Stock':
        return 'bg-success/10 text-success';
      case 'Low Stock':
        return 'bg-warning/10 text-warning';
      case 'Out of Stock':
        return 'bg-error/10 text-error';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-200">
      <div className="relative h-56 overflow-hidden bg-muted">
        <AppImage
          src={product.image}
          alt={product.alt}
          className="w-full h-full object-cover"
        />
        <button
          onClick={() => onSave(product.id)}
          className="absolute top-3 right-3 w-9 h-9 bg-background/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-background transition-colors duration-200"
          aria-label={isSaved ? 'Remove from saved' : 'Save product'}
        >
          <Icon
            name="BookmarkIcon"
            size={20}
            variant={isSaved ? 'solid' : 'outline'}
            className={isSaved ? 'text-accent' : 'text-muted-foreground'}
          />
        </button>
        {product.verified && (
          <div className="absolute top-3 left-3 px-2 py-1 bg-verified text-verified-foreground rounded text-xs font-semibold flex items-center space-x-1">
            <Icon name="CheckBadgeIcon" size={14} variant="solid" />
            <span>Verified</span>
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-headline font-semibold text-foreground truncate mb-1">
              {product.name}
            </h3>
            <p className="text-sm text-muted-foreground truncate flex items-center space-x-1">
              <Icon name="BuildingOfficeIcon" size={14} />
              <span>{product.manufacturer}</span>
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-1 mb-3">
          {[...Array(5)].map((_, index) => (
            <Icon
              key={index}
              name="StarIcon"
              size={14}
              variant={index < Math.floor(product.rating) ? 'solid' : 'outline'}
              className={index < Math.floor(product.rating) ? 'text-warning' : 'text-muted'}
            />
          ))}
          <span className="text-xs text-muted-foreground ml-1">({product.rating})</span>
        </div>

        <div className="flex items-center justify-between mb-3 pb-3 border-b border-border">
          <div>
            <p className="text-xs text-muted-foreground">Price Range</p>
            <p className="text-lg font-headline font-bold text-primary">{product.price}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground">MOQ</p>
            <p className="text-sm font-semibold text-foreground">{product.moq}</p>
          </div>
        </div>

        <div className="flex items-center justify-between mb-3">
          <span
            className={`px-2 py-1 rounded text-xs font-semibold ${getAvailabilityColor(
              product.availability
            )}`}
          >
            {product.availability}
          </span>
          <span className="text-xs text-muted-foreground">{product.category}</span>
        </div>

        {product.certifications.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {product.certifications.slice(0, 2).map((cert, index) => (
              <span
                key={index}
                className="px-2 py-0.5 bg-info/10 text-info text-xs rounded border border-info/20"
              >
                {cert}
              </span>
            ))}
            {product.certifications.length > 2 && (
              <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded">
                +{product.certifications.length - 2}
              </span>
            )}
          </div>
        )}

        <div className="flex space-x-2">
          <Link
            href={`/product-catalog/${product.id}`}
            className="flex-1 px-3 py-2 bg-muted text-foreground rounded text-sm font-semibold text-center hover:bg-muted/80 transition-colors duration-200"
          >
            View Details
          </Link>
          <button
            onClick={() => onInquiry(product.id)}
            className="flex-1 px-3 py-2 bg-conversion text-conversion-foreground rounded text-sm font-semibold hover:bg-conversion/90 transition-colors duration-200"
          >
            Inquire Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;